import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomValidators } from '@shared/customValidators';
import { collectFormErrors } from '@shared/formHelpers';
import { ExpectedActivitySurvey } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';

enum UseCase {
    pettyCash = 'Petty Cash',
    travel = 'Travel and entertainment',
    team = 'Team Spending',
    company = 'Company Expenses',
    advertising = 'Online Advertising',
    other = 'Other'
}

interface UseCaseData {
    value: UseCase;
    active: boolean;
    showDescription: boolean;
    label: string;
}

@Component({
  selector: 'use-case',
  templateUrl: './use-case.component.html',
  styles: ``
})
export class UseCaseComponent implements OnInit {
    private readonly subStepName = 'Expected Use Case';
    private expectedActivitySurveyToSave: ExpectedActivitySurvey;
    useCaseDescriptionMaxlength: number;
    form: UntypedFormGroup;
    useCases: UseCaseData[] = [
        { value: UseCase.pettyCash, active: false, showDescription: false, label: 'PETTY_CASH' },
        { value: UseCase.travel, active: false, showDescription: false, label: 'TRAVEL' },
        { value: UseCase.team, active: false, showDescription: false, label: 'TEAM' },
        { value: UseCase.company, active: false, showDescription: false, label: 'COMPANY' },
        { value: UseCase.advertising, active: false, showDescription: false, label: 'ADVERTISING' },
        { value: UseCase.other, active: false, showDescription: true, label: 'OTHER' }
    ];
    loading = false;

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const expectedActivitySurvey = this.route.snapshot.data.expectedActivitySurvey;
        this.expectedActivitySurveyToSave = {
            ...expectedActivitySurvey
        };
        this.useCaseDescriptionMaxlength = CustomValidators.getExpectedActivitySurveyMaxlength('reasonOfUseDescription');
        const activeUseCases = expectedActivitySurvey?.reasonOfUse?.split(';') ?? [];
        this.form = this.fb.group({});
        for (const useCase of this.useCases) {
            useCase.active = activeUseCases.includes(useCase.value);
            this.form.addControl(useCase.label, this.fb.control(useCase.active));
        }
        if (this.useCases.find(useCase => useCase.showDescription).active) {
            this.showDescription(expectedActivitySurvey?.reasonOfUseDescription);
        }
    }

    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep() {
        if (this.isValid()) {
            this.loading = true;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            const useCases: UseCase[] = [];
            for (const useCase of this.useCases) {
                if (this.form.get(useCase.label).value) {
                    useCases.push(useCase.value);
                    if (useCase.showDescription) {
                        this.expectedActivitySurveyToSave.reasonOfUseDescription = this.form.get('description').value;
                    }
                } else if (useCase.showDescription) {
                    this.expectedActivitySurveyToSave.reasonOfUseDescription = null;
                }
            }
            this.expectedActivitySurveyToSave.reasonOfUse = useCases.join(';');
            try {
                await this.registrationService.saveExpectedActivitySurvey(this.expectedActivitySurveyToSave);
                this.trackSubStepCompleted();
                this.registrationService.completeStep(REGISTRATION_STEP_ID.USE_CASE);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            } catch (e) {
                this.logger.error('Save expected activity survey failed', e, 'with expected activity survey', this.expectedActivitySurveyToSave);
                this.loading = false;
                this.dynatrace.leaveAction(submitActionId);
            }
        } else {
            this.form.markAllAsTouched();
            this.amplitudeService.trackFormSubmissionRejected(
                this.route.snapshot.data['pageName'],
                collectFormErrors(this.form, this.atLeastOneSelected() ? {} : { useCase: 'selectOneOrMore' })
            );
        }
    }

    updateCheck(useCase: UseCaseData, checked: boolean) {
        this.form.markAllAsTouched();
        if (useCase.showDescription) {
            if (checked) {
                this.showDescription();
            } else {
                this.removeDescription();
            }
        }
    }

    private isValid(): boolean {
        return this.form.valid && this.atLeastOneSelected();
    }

    atLeastOneSelected(): boolean {
        return Object.keys(this.form.controls).some(key => {
            if (key === 'description') {
                return false;
            }
            return this.form.get(key).value;
        });
    }

    private showDescription(description: string = null): void {
        if (!this.form.controls['description']) {
            this.amplitudeService.trackFreeTextSurveyOptionSelected(this.subStepName);
            this.form.addControl('description', this.fb.control(description, [
                Validators.required,
                Validators.maxLength(this.useCaseDescriptionMaxlength)
            ]));
        }
    }

    private removeDescription(): void {
        if (this.form.controls['description']) {
            this.form.removeControl('description');
        }
    }

}
